import { useState } from "react";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// Consistent field styling used across all auth forms.
const fieldSx = {
  mb: 2,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    "& fieldset": { borderColor: "divider", borderWidth: 1.5 },
    "&:hover fieldset": { borderColor: "primary.main" },
    "&.Mui-focused fieldset": { borderColor: "primary.main", borderWidth: 2 },
  },
  "& .MuiInputLabel-root": { fontSize: { xs: 14, sm: 15 }, fontWeight: 500 },
};

// Presets wire the correct mobile keyboard + autocomplete per field type.
const presets = {
  phone: { type: "tel", inputMode: "tel", autoComplete: "tel" },
  email: { type: "email", inputMode: "email", autoComplete: "email" },
  numeric: { type: "text", inputMode: "numeric", autoComplete: "one-time-code" },
};

/**
 * Themed TextField wrapper.
 * - `error` is coerced to boolean (MUI expects bool, not the RHF error object).
 * - `variant="phone" | "email" | "numeric"` applies the right mobile keyboard.
 * - `variant="password"` adds a built-in show/hide toggle.
 */
function AuthTextField({
  variant,
  error,
  helperText,
  type,
  sx,
  InputProps,
  inputProps,
  autoComplete,
  ...rest
}) {
  const isPassword = variant === "password";
  const [show, setShow] = useState(false);
  const preset = presets[variant] || {};

  const resolvedType = isPassword
    ? show
      ? "text"
      : "password"
    : type || preset.type || "text";

  return (
    <TextField
      fullWidth
      size="small"
      type={resolvedType}
      error={!!error}
      helperText={helperText}
      autoComplete={
        autoComplete ||
        (isPassword ? "current-password" : preset.autoComplete)
      }
      inputProps={{
        ...(preset.inputMode ? { inputMode: preset.inputMode } : {}),
        ...inputProps,
      }}
      InputProps={{
        ...(isPassword && {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                edge="end"
                onClick={() => setShow((s) => !s)}
                aria-label={show ? "Hide password" : "Show password"}
              >
                {show ? (
                  <Visibility fontSize="small" />
                ) : (
                  <VisibilityOff fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }),
        ...InputProps,
      }}
      sx={{ ...fieldSx, ...sx }}
      {...rest}
    />
  );
}

export default AuthTextField;
